import PersonalAccountBeneficiaryApiClient from "@/api/personalAccountBeneficiaryApiClient";
import { PersonalAccountBeneficiary } from "@/types";
import {
  AppToasterV2,
  BaseFieldProps,
  CoreFieldValue,
  hashStringSHA256,
  useFieldSchemaV2FactoryContext,
} from "@ucl/library";
import { isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { AxiosError } from "axios";
import { createPersonalAccountBeneficiaryFormBuilder } from "@/components/PersonalAccountBeneficiaries/PersonalAccountBeneficiaryFormBuilder";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";
import { FinancialApplicationFormSectionContextType } from "./useFinancialApplicationFormSectionValidation";

export const usePersonalAccountBeneficiary = (
  financialApplicationId: string,
  personalAccountBeneficiaryId: string,
  executionContextType: FinancialApplicationFormSectionContextType = FinancialApplicationFormSectionContextType.Applicant
) => {
  const [formValue, setFormValueInternal] = useState<PersonalAccountBeneficiary | undefined>();
  // Hash of the form value
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const personalAccountBeneficiaryApiClient = useAuthenticatedApiClient(
    PersonalAccountBeneficiaryApiClient
  );

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await saveFormData(formValue);
      }
    },
    debounceDelay: 1000,
  });

  useEffect(() => {
    getPersonalAccountBeneficiary();
  }, [financialApplicationId, personalAccountBeneficiaryId]);

  const getPersonalAccountBeneficiary = async () => {
    setIsLoading(true);
    try {
      const beneficiary = await personalAccountBeneficiaryApiClient.getPersonalAccountBeneficiary(
        financialApplicationId,
        personalAccountBeneficiaryId
      );
      setFormValueInternal(beneficiary);
      const beneficiaryHash = await hashStringSHA256(JSON.stringify(beneficiary));
      setFormValueHash(beneficiaryHash);
    } catch (error) {
      console.error("Failed to get personal account beneficiary:", error);
      AppToasterV2.showFailedActionToaster("Failed to load personal account beneficiary.");
    } finally {
      setIsLoading(false);
    }
  };

  const setFormValue = async (
    value: PersonalAccountBeneficiary,
    newFieldValue: CoreFieldValue,
    _fieldProps: BaseFieldProps<CoreFieldValue>
  ) => {
    if (isNil(newFieldValue) || isNil(value.personInformation) || isNil(value)) {
      return;
    }

    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (formValueHash === newHash) {
      return;
    }

    setFormValueInternal({ ...value });

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const saveFormData = useCallback(
    async (data: PersonalAccountBeneficiary) => {
      if (isSaving) return;

      setIsSaving(true);
      try {
        await personalAccountBeneficiaryApiClient.updatePersonalAccountBeneficiary(
          financialApplicationId,
          personalAccountBeneficiaryId,
          data
        );

        const newHash = await hashStringSHA256(JSON.stringify(data));
        setFormValueHash(newHash);
        await refreshFinancialApplication();
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Failed to save personal account beneficiary form data:", error);
        AppToasterV2.showFailedActionToaster("Failed to save personal account beneficiary.");
      } finally {
        setIsSaving(false);
      }
    },
    [
      financialApplicationId,
      personalAccountBeneficiaryId,
      personalAccountBeneficiaryApiClient,
      isSaving,
    ]
  );

  const refresh = useCallback(() => {
    getPersonalAccountBeneficiary();
  }, [financialApplicationId, personalAccountBeneficiaryId]);

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createPersonalAccountBeneficiaryFormBuilder(schemaFactory, executionContextType);
  }, [schemaFactory]);

  return {
    formValue,
    setFormValue,
    saveFormData,
    isLoading,
    isSaving,
    refresh,
    formBuilder,
  };
};
